import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Eye, Loader2, Link2, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function parseBlueprint(content) {
  const rows = [];
  let title = null;
  (content || "").split("\n").forEach((line) => {
    const idx = line.indexOf(":");
    if (idx > 0 && idx < 24) {
      const label = line.slice(0, idx).trim();
      const value = line.slice(idx + 1).trim();
      if (label.toUpperCase() === "APP NAME") title = value;
      else if (value) rows.push({ label, value });
    } else if (line.trim()) {
      rows.push({ label: null, value: line.trim() });
    }
  });
  return { title, rows };
}

export default function BlueprintView() {
  const { slug } = useParams();
  const [bp, setBp] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    fetch(`${API}/blueprints/${slug}`)
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then((d) => {
        setBp(d);
        setStatus("ok");
      })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied to clipboard!");
  };

  const parsed = bp ? parseBlueprint(bp.content) : { title: null, rows: [] };
  const isApp = !!bp && (bp.kind === "app" || (bp.content || "").trim().toLowerCase().startsWith("<!doctype"));
  const title = (isApp ? bp?.title : parsed.title || bp?.title) || "App Blueprint";

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 right-0 w-[40rem] h-[40rem] bg-[#0055FF]/15 blur-[140px] rounded-full" />
      <div className="pointer-events-none absolute bottom-0 -left-32 w-[34rem] h-[34rem] bg-[#00F0FF]/8 blur-[130px] rounded-full" />

      <header className="relative z-10 max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="bp-home-link">
          <div className="w-9 h-9 rounded-xl bg-[#0055FF] flex items-center justify-center font-display font-extrabold text-lg shadow-[0_0_25px_rgba(0,85,255,0.6)]">A</div>
          <span className="font-display text-xl font-bold tracking-tight">ArcaCentum<span className="text-[#00F0FF]">.ai</span></span>
        </Link>
        <Link to="/" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-slate-400 hover:text-white transition-colors duration-300" data-testid="bp-back-link">
          <ArrowLeft size={14} /> Home
        </Link>
      </header>

      <main className="relative z-10 max-w-4xl mx-auto px-6 pt-10 pb-24">
        {status === "loading" && (
          <div className="flex items-center justify-center gap-2 text-slate-400 py-40" data-testid="bp-loading">
            <Loader2 className="animate-spin text-[#00F0FF]" /> Loading blueprint…
          </div>
        )}

        {status === "notfound" && (
          <div className="text-center py-40" data-testid="bp-notfound">
            <h1 className="font-display font-bold text-4xl mb-4">Blueprint not found</h1>
            <p className="text-slate-400 mb-8">This link may have expired or never existed.</p>
            <Link to="/" className="inline-flex items-center gap-2 bg-white text-black px-7 py-3.5 rounded-2xl font-semibold hover:bg-[#00F0FF] transition-colors duration-300">
              Build your own <ArrowRight size={16} />
            </Link>
          </div>
        )}

        {status === "ok" && bp && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} data-testid="bp-content">
            {isApp ? (
              <>
                <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-[#00F0FF] mb-6">
                  <Sparkles size={14} /> live app · built with ArcaCentum
                </div>
                <h1 className="font-display font-extrabold text-5xl md:text-6xl tracking-tighter leading-[0.95] text-glow" data-testid="bp-title">
                  {title}
                </h1>
                <div className="flex items-center gap-5 mt-6 text-slate-500 font-mono text-xs">
                  <span className="inline-flex items-center gap-1.5" data-testid="bp-views"><Eye size={14} /> {bp.views} views</span>
                  <button onClick={copyLink} className="inline-flex items-center gap-1.5 hover:text-white transition-colors duration-300" data-testid="bp-copy-link">
                    <Link2 size={14} /> Copy link
                  </button>
                </div>
                <div className="mt-10 flex justify-center">
                  <div className="w-full max-w-[420px] h-[640px] rounded-[2.2rem] border-4 border-zinc-800 overflow-hidden shadow-2xl bg-black">
                    <iframe
                      title={title}
                      srcDoc={bp.content}
                      className="w-full h-full"
                      sandbox="allow-scripts allow-forms allow-modals allow-popups allow-same-origin"
                      data-testid="bp-app-iframe"
                    />
                  </div>
                </div>
                <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-5 max-w-[420px] mx-auto">
                  <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-1.5">Prompt</p>
                  <p className="text-slate-400 italic">"{bp.prompt}"</p>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-[#00F0FF] mb-6">
                  <Sparkles size={14} /> generated blueprint
                </div>
                <h1 className="font-display font-extrabold text-5xl md:text-6xl tracking-tighter leading-[0.95] text-glow" data-testid="bp-title">
                  {title}
                </h1>

                <div className="flex items-center gap-5 mt-6 text-slate-500 font-mono text-xs">
                  <span className="inline-flex items-center gap-1.5" data-testid="bp-views"><Eye size={14} /> {bp.views} views</span>
                  <button onClick={copyLink} className="inline-flex items-center gap-1.5 hover:text-white transition-colors duration-300" data-testid="bp-copy-link">
                    <Link2 size={14} /> Copy link
                  </button>
                </div>

                <div className="mt-10 rounded-3xl border border-white/10 bg-[#0a0a0a] divide-y divide-white/5" data-testid="bp-rows">
                  {parsed.rows.map((row, i) => (
                    <div key={i} className="p-6 md:p-7">
                      {row.label && (
                        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#00F0FF] mb-2">{row.label}</p>
                      )}
                      <p className="text-slate-100 text-lg leading-relaxed">{row.value}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                  <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-1.5">Prompt</p>
                  <p className="text-slate-400 italic">"{bp.prompt}"</p>
                </div>
              </>
            )}

            <div className="mt-14 rounded-3xl bg-gradient-to-br from-[#0055FF] to-[#0038a8] p-10 text-center">
              <h2 className="font-display font-bold text-3xl md:text-4xl mb-3">Like this? Build your own.</h2>
              <p className="text-white/80 mb-7 max-w-md mx-auto">Describe any app and watch ArcaCentum build a real, working version in seconds.</p>
              <Link to="/#chat" className="inline-flex items-center gap-2 bg-white text-black px-8 py-4 rounded-2xl font-semibold hover:bg-black hover:text-white transition-colors duration-300" data-testid="bp-build-cta">
                Try the builder <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
